import { Fragment } from "react";

import type { ChatMessage } from "../api/types";
import { summaryDividerAfter } from "../summary";
import { Markdown } from "./Markdown";

interface Props {
  messages: ChatMessage[];
  streaming: string;
  /** Model writing the live reply, from the run's model map. */
  streamingModel?: string | undefined;
  activeNodes: { node: string; at: number }[];
  /** The thread's `summary_through`: messages up to it are sent as a summary. */
  summarizedThrough?: unknown;
}

export function Transcript({
  messages,
  streaming,
  streamingModel,
  activeNodes,
  summarizedThrough,
}: Props) {
  const divider = summaryDividerAfter(messages, summarizedThrough);
  return (
    <div className="transcript">
      {messages.map((message, index) => (
        <Fragment key={message.id ?? index}>
          <article className={`msg msg--${message.type}`}>
            <span className="msg__role">
              {label(message)}
              {message.model && (
                <span className="msg__model">{message.model}</span>
              )}
            </span>
            <div
              className={`msg__body${message.type === "ai" ? " msg__body--md" : ""}`}
            >
              {!message.content ? (
                <em className="muted">(no text)</em>
              ) : message.type === "ai" ? (
                <Markdown text={message.content} />
              ) : (
                message.content
              )}
              {message.tool_calls?.map((call) => (
                <pre key={call.id} className="toolcall">
                  {call.name}({JSON.stringify(call.args)})
                </pre>
              ))}
            </div>
          </article>
          {index === divider && (
            <p className="muted small summary-divider">
              Earlier turns summarized: the assistant now works from a summary
              of the conversation above this line.
            </p>
          )}
        </Fragment>
      ))}

      {streaming && (
        <article className="msg msg--ai msg--live">
          <span className="msg__role">
            assistant
            {streamingModel && (
              <span className="msg__model">{streamingModel}</span>
            )}
          </span>
          <div className="msg__body msg__body--md">
            <Markdown text={streaming} />
            <span className="caret" />
          </div>
        </article>
      )}

      {activeNodes.length > 0 && (
        <p className="muted nodes">
          {activeNodes.slice(-6).map((n, i) => (
            <span key={`${n.node}-${n.at}-${i}`} className="chip">
              {n.node}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}

function label(message: ChatMessage): string {
  if (message.type === "human") return "you";
  if (message.type === "ai") return "assistant";
  if (message.type === "tool") return `tool · ${message.name ?? ""}`;
  return message.type;
}
